import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

// Components
import FavoriteIcon from '@icons/FavoriteIcon';
import MailIcon from '@icons/MailIcon';
import ShareIcon from '@icons/ShareIcon';
import CardMemberAvatar from '@atoms/CardMemberAvatar';
import { BodySmall, H4, LabelLarge } from '@styled';
import { PropertyChip } from '../PropertyChip';

// Functionality
import getFavoriteHook from '@hooks/getFavoriteHook';

const Container = styled.div`
    height: 450px;
    margin-bottom: auto;
`;

const PropertyCard = props => {
    const {
        id,
        recordId,
        activatedAt,
        activations,
        // address1,
        // address2,
        coverPhotoUrl,
        displayLocation,
        expiresAt,
        internalUrl,
        isFavorite: initialIsFavorite,
        manager,
        populationDensity,
        purpose,
        size,
        squareFeet,
        title,
        typeOfCenter,
        url,
        onClick = () => {},
        isLoggedIn = true,
        openInNewTab = false,
        showShareIcon = false,
        hit,
        favorited,
    } = props;

    const useFavorite = getFavoriteHook({ type: 'property', initialIsFavorite });
    const { isFavorite, isLoadingFavorite, saveFavorite, removeFavorite, setIsFavoriteManually } = useFavorite();

    useEffect(() => {
        setIsFavoriteManually(initialIsFavorite);
    }, [initialIsFavorite, setIsFavoriteManually]);

    const onFavorite = () => {
        if (isFavorite) {
            removeFavorite(recordId);
        } else {
            saveFavorite(recordId);
            favorited({ hit });
        }
    };

    const imageBlock = (
        <>
            <Link
                to={internalUrl}
                className="card-link"
                style={{ position: 'relative' }}
                onClick={e => {
                    if (hit) {
                        onClick({ hit }); // Algolia
                    } else {
                        onClick({ props }); // Apollo/GraphQL
                    }

                    if (openInNewTab) {
                        e.preventDefault();
                        window.open(internalUrl, '_blank');
                    }
                }}
            >
                {coverPhotoUrl && <img className="card-img-top w-100" src={coverPhotoUrl} alt={title} />}
                {!coverPhotoUrl && (
                    <div className="no-image bg-gray-lighter">
                        <img
                            src="/assets/images/company-default.svg"
                            alt=""
                            className="card-img-top w-100 no-image-icon"
                        />
                    </div>
                )}

                <div style={{ position: 'absolute', top: '24px', left: '24px' }}>
                    <PropertyChip activatedAt={activatedAt} expiresAt={expiresAt} activations={activations} />
                </div>
            </Link>
        </>
    );

    return (
        <Container className="card">
            {imageBlock}
            <div className="py-0 card-block">
                <Link
                    to={internalUrl}
                    className="card-link"
                    onClick={e => {
                        if (hit) {
                            onClick({ hit }); // Algolia
                        } else {
                            onClick({ props }); // Apollo/GraphQL
                        }

                        if (openInNewTab) {
                            e.preventDefault();
                            window.open(internalUrl, '_blank');
                        }
                    }}
                >
                    <H4
                        className="display6 bold mt-3 mb-0.5rem"
                        style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                    >
                        {title}
                    </H4>

                    <div
                        className="mb-1rem"
                        style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                    >
                        <LabelLarge>{displayLocation}</LabelLarge>
                    </div>

                    <div className="mb-1rem text-dark-gray">
                        <BodySmall>{purpose}</BodySmall>
                        <BodySmall>{populationDensity}</BodySmall>
                        <BodySmall>{typeOfCenter}</BodySmall>
                        {squareFeet && <BodySmall>{squareFeet.toLocaleString()} sq ft</BodySmall>}
                        {!squareFeet && <BodySmall>{size}</BodySmall>}
                    </div>

                    <div className="d-flex align-items-center">
                        {manager && (
                            <>
                                <CardMemberAvatar
                                    photoURL={manager.photoUrl}
                                    firstName={manager.firstName}
                                    lastName={manager.lastName}
                                    fullName={`${manager.firstName} ${manager.lastName}`}
                                    sizeMobile={26}
                                    sizeDesktop={26}
                                />
                                <div className="ml-2">
                                    <BodySmall>{`${manager.firstName} ${manager.lastName}`}</BodySmall>
                                </div>
                            </>
                        )}
                    </div>
                </Link>
            </div>
            <div className="py-3 pl-3 pr-3 card-icons w-100 d-flex justify-content-end align-items-center">
                {showShareIcon && (
                    <div className="mr-1rem">
                        <ShareIcon title={title} url={internalUrl} />
                    </div>
                )}

                <MailIcon
                    title={title}
                    url={internalUrl}
                    id={recordId}
                    displayLocation={displayLocation}
                    isFavorite={isFavorite}
                    isLoggedIn={isLoggedIn}
                    companyID={manager.companyID}
                    firstName={manager.firstName}
                    lastName={manager.lastName}
                    fullName={manager.fullName}
                    companyName={manager.companyName}
                />

                <FavoriteIcon
                    id={id}
                    type="property"
                    isLoggedIn={isLoggedIn}
                    isFavorite={isFavorite}
                    isLoading={isLoadingFavorite}
                    tooltipLabel="Property"
                    onClick={onFavorite}
                />
            </div>
        </Container>
    );
};

PropertyCard.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    recordId: PropTypes.number,
    activations: PropTypes.number.isRequired,
    activatedAt: PropTypes.string,
    // address1: PropTypes.string,
    // address2: PropTypes.string,
    coverPhotoUrl: PropTypes.string,
    displayLocation: PropTypes.string,
    expiresAt: PropTypes.string,
    internalUrl: PropTypes.string,
    isFavorite: PropTypes.bool,
    manager: PropTypes.object,
    populationDensity: PropTypes.string,
    purpose: PropTypes.string,
    size: PropTypes.string,
    squareFeet: PropTypes.number,
    title: PropTypes.string.isRequired,
    typeOfCenter: PropTypes.string,
    url: PropTypes.string,
    onClick: PropTypes.func,
    isLoggedIn: PropTypes.bool,
    openInNewTab: PropTypes.bool,
    showShareIcon: PropTypes.bool,
    hit: PropTypes.object,
    favorited: PropTypes.func,
};

export default PropertyCard;
